(ns ui.preferences.page
  "Replicant page for user preferences. Accessible at `/ui/preferences`.

   Sections:
   - General Settings (email, disconnect warning toggle, change email)
   - Connected Integrations (Canva)
   - Beta Features (feature flag toggles)"
  (:require [parts.replicant.query :as query]
            [storrito.connect3.info-panel :as info-panel]
            [ui.app-frame :as app-frame]))

(def preferences-query
  {:query/kind :query/preferences})

(defn- toggle-command
  [state]
  (let [result (query/get-result state preferences-query)]
    {:command/kind :command/team-toggle-disconnect-warning-receiver
     :command/data {:email (:user/email result)}}))

(defn- canva-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 24 24"
         :class ["w-8" "h-8"]}
   [:path {:fill "#00C4CC" :d "M12 22a10 10 0 110-20 10 10 0 010 20z"}]
   [:path {:fill-rule "evenodd" :clip-rule "evenodd"
           :d "M16.1 14a.5.5 0 00-.4-.4.4.4 0 00-.4.2l-.1.2-.1.2c-.5.7-.8 1-1.7 1.5-.4.2-.9.3-1.3.3-1.3 0-2-1.1-2.3-2.1-.4-1.7.3-3.9 1.3-5.1.5-.7 1-1.1 1.6-1.1a.9.9 0 01.7.4c.3.4.4.8 0 1.4a.5.5 0 00.2.6c.3.1.6 0 1-.5.3-.5.3-1.3 0-1.8-.4-.7-1.2-1-2-1-.7 0-1.3.1-1.8.4a6.8 6.8 0 00-2.8 7c.3 1 .8 1.8 1.4 2.3a4 4 0 002.3.8c1.1 0 2-.4 2.5-.8a5.6 5.6 0 001.7-1.8l.1-.2a.7.7 0 00.1-.5zm-4.4 3z"
           :fill "#fff"}]])

(defn- toggle-switch
  [{:keys [checked on-toggle]}]
  [:button
   {:class (into ["relative" "inline-flex" "h-6" "w-11" "shrink-0"
                  "cursor-pointer" "rounded-full" "border-2"
                  "border-transparent" "transition-colors" "duration-200"
                  "ease-in-out" "focus:outline-none" "focus:ring-2"
                  "focus:ring-indigo-600" "focus:ring-offset-2"]
                 (if checked
                   ["bg-indigo-600"]
                   ["bg-gray-200"]))
    :role "switch"
    :aria-checked (str (boolean checked))
    :on {:click on-toggle}}
   [:span
    {:class (into ["pointer-events-none" "inline-block" "h-5" "w-5"
                   "rounded-full" "bg-white" "shadow" "ring-0"
                   "transition" "duration-200" "ease-in-out"]
                  (if checked
                    ["translate-x-5"]
                    ["translate-x-0"]))}]])

(defn- section-card
  "Renders a card with a header and a vector of child elements.
   `children` must be a vector of hiccup forms."
  [{:keys [title description icon]} children]
  [:div
   {:class ["bg-white" "rounded-xl" "shadow-sm" "border" "border-gray-200"
            "overflow-hidden"]}
   [:div
    {:class ["px-6" "py-5" "border-b" "border-gray-100"]}
    [:div
     {:class ["flex" "items-center" "gap-3"]}
     (when icon
       [:div
        {:class ["flex" "items-center" "justify-center" "w-10" "h-10"
                 "rounded-lg" "bg-indigo-50" "text-indigo-600"]}
        icon])
     [:div
      [:h2
       {:class ["text-lg" "font-semibold" "text-gray-900"]}
       title]
      (when description
        [:p
         {:class ["text-sm" "text-gray-500" "mt-0.5"]}
         description])]]]
   (into [:div
          {:class ["divide-y" "divide-gray-100"]}]
         children)])

(defn- setting-row
  [{:keys [label description action]}]
  [:div
   {:class ["px-6" "py-4" "flex" "items-center" "justify-between" "gap-4"]}
   [:div
    {:class ["min-w-0" "flex-1"]}
    [:div
     {:class ["text-sm" "font-medium" "text-gray-900"]}
     label]
    (when description
      [:div
       {:class ["text-sm" "text-gray-500" "mt-0.5"]}
       description])]
   [:div
    {:class ["shrink-0"]}
    action]])

(defn- email-section
  [state]
  (let [result (query/get-result state preferences-query)
        email (:user/email result)]
    (setting-row
      {:label "Email address"
       :description email
       :action
       [:a
        {:href "/ui/team/"
         :class ["inline-flex" "items-center" "gap-1.5" "text-sm"
                 "font-medium" "text-indigo-600" "hover:text-indigo-500"]}
        "Change"
        [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
               :fill "currentColor" :class ["w-4" "h-4"]}
         [:path {:fill-rule "evenodd"
                 :d "M5 10a.75.75 0 01.75-.75h6.638L10.23 7.29a.75.75 0 111.04-1.08l3.5 3.25a.75.75 0 010 1.08l-3.5 3.25a.75.75 0 11-1.04-1.08l2.158-1.96H5.75A.75.75 0 015 10z"
                 :clip-rule "evenodd"}]]]})))

(defn- disconnect-warning-section
  [state]
  (let [result (query/get-result state preferences-query)
        enabled (:disconnect-warning-enabled? result)
        cmd (toggle-command state)]
    (setting-row
      {:label "Disconnect warning emails"
       :description "Receive an email when an Instagram account gets disconnected"
       :action
       (toggle-switch
         {:checked enabled
          :on-toggle [[:data/command
                       cmd
                       {:on-success [[:data/query preferences-query]]
                        :on-error [[:ui/show-error]]}]]})})))

(defn- canva-account-row
  [account]
  (let [disconnect-cmd {:command/kind :command/canva-disconnect
                        :command/data {:canva-user-id (:canva.user/id account)}}]
    [:div
     {:class ["px-6" "py-4" "flex" "items-center" "justify-between" "gap-4"]
      :replicant/key (:canva.user/id account)}
     [:div
      {:class ["flex" "items-center" "gap-3" "min-w-0"]}
      [:div
       {:class ["shrink-0"]}
       (canva-icon)]
      [:div
       {:class ["min-w-0"]}
       [:div
        {:class ["text-sm" "font-medium" "text-gray-900" "truncate"]}
        (:canva.user/display-name account)]
       (when-let [email (:canva.user/email account)]
         [:div
          {:class ["text-sm" "text-gray-500" "truncate"]}
          email])]]
     [:button
      {:class ["inline-flex" "items-center" "gap-1.5" "text-sm"
               "font-medium" "text-red-600" "hover:text-red-500"
               "cursor-pointer"]
       :on {:click [[:js/confirm "Disconnect this Canva account?"]
                    [:data/command
                     disconnect-cmd
                     {:on-success [[:data/query preferences-query]]
                      :on-error [[:ui/show-error]]}]]}}
      [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
             :fill "currentColor" :class ["w-4" "h-4"]}
       [:path {:d "M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"}]]
      "Disconnect"]]))

(defn- canva-empty-state
  []
  [:div
   {:class ["px-6" "py-4" "flex" "items-center" "justify-between" "gap-4"]}
   [:div
    {:class ["flex" "items-center" "gap-3" "min-w-0"]}
    [:div {:class ["shrink-0"]} (canva-icon)]
    [:div
     {:class ["text-sm" "text-gray-500"]}
     "Connect your Canva account to import designs"]]
   [:button
    {:class ["inline-flex" "items-center" "gap-2" "px-3" "py-1.5" "text-sm"
             "font-medium" "rounded-lg" "border" "border-gray-300"
             "text-gray-700" "bg-white" "hover:bg-gray-50"
             "cursor-pointer" "shadow-sm" "shrink-0"]
     :on {:click [[:data/command
                   {:command/kind :command/canva-connect
                    :command/data {}}
                   {:on-success [[:editor2/canva-connect-success]]}]]}}
    "Connect"]])

(defn- integrations-content
  [state]
  (let [result (query/get-result state preferences-query)
        accounts (:canva-accounts result)]
    (if (seq accounts)
      (vec (map canva-account-row accounts))
      [(canva-empty-state)])))

(defn- feature-flag-row
  [flag]
  (let [cmd-kind (if (:enabled flag)
                   :command/feature-flag-disable
                   :command/feature-flag-enable)
        cmd {:command/kind cmd-kind
             :command/data {:feature-flag (:id flag)}}]
    (setting-row
      {:label (:name flag)
       :description (:description flag)
       :action
       (toggle-switch
         {:checked (:enabled flag)
          :on-toggle [[:data/command
                       cmd
                       {:on-success [[:data/query preferences-query]]
                        :on-error [[:ui/show-error]]}]]})})))

(defn- feature-flags-content
  [state]
  (let [result (query/get-result state preferences-query)
        flags (:feature-flags result)]
    (mapv (fn [flag]
            [:div {:replicant/key (:id flag)}
             (feature-flag-row flag)])
          flags)))

(defn- gear-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "currentColor" :class ["w-5" "h-5"]}
   [:path {:fill-rule "evenodd"
           :d "M7.84 1.804A1 1 0 018.82 1h2.36a1 1 0 01.98.804l.331 1.652a6.993 6.993 0 011.929 1.115l1.598-.54a1 1 0 011.186.447l1.18 2.044a1 1 0 01-.205 1.251l-1.267 1.113a7.047 7.047 0 010 2.228l1.267 1.113a1 1 0 01.206 1.25l-1.18 2.045a1 1 0 01-1.187.447l-1.598-.54a6.993 6.993 0 01-1.929 1.115l-.33 1.652a1 1 0 01-.98.804H8.82a1 1 0 01-.98-.804l-.331-1.652a6.993 6.993 0 01-1.929-1.115l-1.598.54a1 1 0 01-1.186-.447l-1.18-2.044a1 1 0 01.205-1.251l1.267-1.114a7.05 7.05 0 010-2.227L1.821 7.773a1 1 0 01-.206-1.25l1.18-2.045a1 1 0 011.187-.447l1.598.54A6.993 6.993 0 017.51 3.456l.33-1.652zM10 13a3 3 0 100-6 3 3 0 000 6z"
           :clip-rule "evenodd"}]])

(defn- link-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "currentColor" :class ["w-5" "h-5"]}
   [:path {:d "M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z"}]
   [:path {:d "M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z"}]])

(defn- beaker-icon []
  [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
         :fill "currentColor" :class ["w-5" "h-5"]}
   [:path {:fill-rule "evenodd"
           :d "M8.5 3.528v4.644c0 .729-.29 1.428-.805 1.944l-1.217 1.216a8.75 8.75 0 00-.983 1.168H14.5a8.754 8.754 0 00-.978-1.168l-1.217-1.216a2.75 2.75 0 01-.805-1.944V3.528a40.205 40.205 0 00-3 0zm-1.5 0a41.728 41.728 0 016 0V2.75A.75.75 0 0012.25 2h-4.5A.75.75 0 007 2.75v.778zM4.19 14.428a6.75 6.75 0 00-.813 1.073.75.75 0 00.656 1.099h11.934a.75.75 0 00.656-1.099 6.748 6.748 0 00-.813-1.073L14.5 13.217a1.25 1.25 0 01-.366-.884V3.528a41.69 41.69 0 00-8.268 0v8.805c0 .332-.132.65-.366.884l-1.31 1.211z"
           :clip-rule "evenodd"}]])

(defn render
  [state]
  (let [loading (query/loading? state preferences-query)
        result (query/get-result state preferences-query)
        flags (:feature-flags result)]
    (app-frame/app-frame
      state
      (if loading
        [:div
         {:class ["flex" "items-center" "justify-center" "py-20"]}
         [:div {:class ["text-gray-400"]} "Loading..."]]
        [:div
         {}
         [:div
          {:class ["mb-6"]}
          [:h1
           {:class ["text-2xl" "font-bold" "text-gray-900"]}
           "Preferences"]]

         [:div
          {:class ["space-y-6"]}

          (section-card
            {:title "General"
             :icon (gear-icon)}
            (cond-> [(email-section state)
                     (disconnect-warning-section state)]
              (or (info-panel/dismissed? {})
                  (info-panel/dismissed? {:reconnect true}))
              (conj (setting-row
                      {:label "Connection tips"
                       :description "Show tips and warnings before connecting an Instagram account"
                       :action
                       [:button
                        {:class ["inline-flex" "items-center" "gap-1.5" "text-sm"
                                 "font-medium" "text-indigo-600" "hover:text-indigo-500"
                                 "cursor-pointer"]
                         :on {:click [[:connect3-info/reset-dismissed]]}}
                        "Reset"]}))))

          (section-card
            {:title "Connected Integrations"
             :icon (link-icon)}
            (integrations-content state))

          (when (seq flags)
            (section-card
              {:title "Beta Features"
               :description "Experimental features for your team. We'd love your feedback."
               :icon (beaker-icon)}
              (feature-flags-content state)))]]))))

(def page
  {:page-id :pages/preferences
   :route [["ui" "preferences"]]
   :on-load (fn [_location]
              [[:ui/hide-sidebar-on-mobile]
               [:data/query preferences-query]])
   :render #'render})
